import * as React from "react";
import { useConvex, useMutation, useQuery } from "convex/react";
import { useNavigate } from "@tanstack/react-router";
import { Bell, BellDot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { api } from "@/lib/convex-api";
import { projectRoute } from "@/lib/app-routes";
import { formatUserFacingError } from "@/lib/errorMessages";
import { organizationContext } from "@/lib/organization-context";
import { cn } from "@/lib/utils";
import type { Id } from "../../../../backend/convex/_generated/dataModel";

function formatRelativeTime(timestamp: number): string {
  const diffMs = Math.max(0, Date.now() - timestamp);
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${diffDay}d ago`;
}

function titleCaseRole(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function NotificationsBell({ className }: { className?: string }) {
  const convex = useConvex();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [actingInviteId, setActingInviteId] = React.useState<string | null>(
    null,
  );
  const [actingAction, setActingAction] = React.useState<
    "accept" | "reject" | null
  >(null);

  const attentionCount = useQuery(api.notifications.getAttentionCount) ?? 0;
  const notifications =
    useQuery(api.notifications.listForMe, open ? {} : "skip") ?? [];
  const acceptInvite = useMutation(api.notifications.acceptInvite);
  const rejectInvite = useMutation(api.notifications.rejectInvite);

  const handleAction = async (
    inviteId: Id<"membershipInvites">,
    action: "accept" | "reject",
  ) => {
    setActingInviteId(inviteId);
    setActingAction(action);
    try {
      if (action === "accept") {
        const result = await acceptInvite({ inviteId });
        organizationContext.setSelectedOrganizationId(result.workspaceId);
        setOpen(false);
        toast.success("Invite accepted.");
        if (result.scopeType === "project" && result.projectId) {
          const project = await convex.query(api.projects.getById, {
            id: result.projectId,
          });
          if (!project?.organizationSlug || !project.slug) {
            throw new Error("Accepted project route details were unavailable.");
          }
          await navigate({
            to: projectRoute,
            params: {
              orgSlug: project.organizationSlug,
              id: project.slug,
            },
          });
          return;
        }
        await navigate({
          to: "/",
        });
      } else {
        await rejectInvite({ inviteId });
        toast.success("Invite declined.");
      }
    } catch (error) {
      toast.error(
        formatUserFacingError(
          error,
          action === "accept"
            ? "Failed to accept invite."
            : "Failed to decline invite.",
        ),
      );
    } finally {
      setActingInviteId(null);
      setActingAction(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          className={cn("relative", className)}
          aria-label={
            attentionCount > 0
              ? `${attentionCount} notifications need attention`
              : "Notifications"
          }
        >
          {attentionCount > 0 ? (
            <BellDot className="size-4" />
          ) : (
            <Bell className="size-4" />
          )}
          {attentionCount > 0 ? (
            <span className="absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {attentionCount > 99 ? "99+" : attentionCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="border-b px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium">Notifications</p>
              <p className="text-xs text-muted-foreground">
                {attentionCount > 0
                  ? `${attentionCount} item${attentionCount === 1 ? "" : "s"} need attention`
                  : "You're all caught up."}
              </p>
            </div>
          </div>
        </div>

        {notifications.length === 0 ? (
          <div className="px-4 py-6 text-sm text-muted-foreground">
            No pending notifications.
          </div>
        ) : (
          <div className="max-h-112 space-y-3 overflow-y-auto p-3">
            {notifications.map((notification) => {
              const isActing = actingInviteId === notification.id;
              const scopeLabel =
                notification.scopeType === "project" ? "Project" : "Workspace";
              const subjectName =
                notification.scopeType === "project"
                  ? (notification.projectName ?? "Project")
                  : notification.workspaceName;

              return (
                <div
                  key={notification.id}
                  className="rounded-lg border bg-background p-3"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{scopeLabel}</Badge>
                        <span className="text-[11px] text-muted-foreground">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm font-medium">{subjectName}</p>
                      <p className="text-xs text-muted-foreground">
                        {notification.invitedByName} invited you as a{" "}
                        {titleCaseRole(notification.role)}.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-[11px] text-muted-foreground">
                      {notification.email}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="xs"
                        variant="outline"
                        disabled={isActing}
                        onClick={() =>
                          void handleAction(notification.id, "reject")
                        }
                      >
                        {isActing && actingAction === "reject"
                          ? "Declining..."
                          : "Decline"}
                      </Button>
                      <Button
                        size="xs"
                        disabled={isActing}
                        onClick={() =>
                          void handleAction(notification.id, "accept")
                        }
                      >
                        {isActing && actingAction === "accept"
                          ? "Accepting..."
                          : "Accept"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
